/**
 * NoteQueueContent Component
 *
 * Simplified note list view for co-sign and edits queues.
 * No bulk actions, category filter, or date range — just search + paginated list.
 */

import React, { useCallback, useState } from 'react';
import { useFeatureFlag } from '@features/user';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import type { NoteCompletionStatus } from '@store/generated/notes';

import type { UserNoteTypeII } from '../../../types/types';
import { useCosignDisposition } from '../hooks/useCosignDisposition';
import { useNoteQueueList } from '../hooks/useNoteQueueList';

import { NoteListRow } from './NoteListRow';

export interface NoteQueueContentProps {
  title: string;
  completionStatus: NoteCompletionStatus;
  organizationId?: string;
  emptyMessage?: string;
  /** 'mine' scopes the queue to the caller's supervisees and enables
   * dismiss/snooze row actions plus the show-dismissed filter. */
  cosignScope?: 'mine';
}

export const NoteQueueContent: React.FC<NoteQueueContentProps> = ({
  title,
  completionStatus,
  organizationId,
  emptyMessage = 'No notes in this queue.',
  cosignScope,
}) => {
  const theme = useTheme();
  const hasNoteSigning = useFeatureFlag('note_signing');
  const { note: noteMetrics } = useEventTracking();
  const [showDismissed, setShowDismissed] = useState(false);
  const { dismiss, remindTomorrow, restore, isUpdating } = useCosignDisposition();

  const {
    notes,
    isLoading,
    isFetching,
    totalCount,
    totalPages,
    page,
    setPage,
    searchTerm,
    setSearchTerm,
    navigateToNote,
    hasNextPage,
    hasPreviousPage,
    isEmpty,
    isSearchActive,
  } = useNoteQueueList({
    completionStatus,
    organizationId,
    cosignScope,
    includeDismissed: showDismissed,
  });

  const handleToggleShowDismissed = useCallback(
    (checked: boolean) => {
      setShowDismissed(checked);
      noteMetrics?.cosignShowDismissedToggled?.(checked);
    },
    [noteMetrics],
  );

  const isDispositionActive = useCallback((note: UserNoteTypeII) => {
    const disposition = note.cosign_disposition;
    if (!disposition) return false;
    if (disposition.dismissed_at != null) return true;
    return disposition.snoozed_until != null && disposition.snoozed_until > Date.now() / 1000;
  }, []);

  const renderCosignActions = useCallback(
    (note: UserNoteTypeII) => {
      if (isDispositionActive(note)) {
        return (
          <Button
            size="small"
            disabled={isUpdating}
            onClick={() => {
              noteMetrics?.cosignRestoreClicked?.(note.note_id);
              void restore(note.note_id);
            }}
          >
            Restore
          </Button>
        );
      }
      return (
        <>
          <Button
            size="small"
            color="inherit"
            disabled={isUpdating}
            onClick={() => {
              noteMetrics?.cosignSnoozeClicked?.(note.note_id);
              void remindTomorrow(note.note_id);
            }}
          >
            Remind tomorrow
          </Button>
          <Button
            size="small"
            color="inherit"
            disabled={isUpdating}
            onClick={() => {
              noteMetrics?.cosignDismissClicked?.(note.note_id);
              void dismiss(note.note_id);
            }}
          >
            Dismiss
          </Button>
        </>
      );
    },
    [dismiss, remindTomorrow, restore, isUpdating, isDispositionActive, noteMetrics],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    [setSearchTerm],
  );

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
  }, [setSearchTerm]);

  return (
    <>
      {/* Background refresh indicator */}
      {isFetching && !isLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: '1rem',
            right: '1rem',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'background.paper',
            padding: '0.5rem 1rem',
            borderRadius: 2,
            boxShadow: 2,
          }}
        >
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Refreshing...
          </Typography>
        </Box>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
            {title}
          </Typography>
          {!isLoading && (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.25 }}>
              {totalCount.toLocaleString()} {totalCount === 1 ? 'note' : 'notes'}
            </Typography>
          )}
        </Box>

        {cosignScope && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showDismissed}
                onChange={e => handleToggleShowDismissed(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Show dismissed</Typography>}
          />
        )}
      </Box>

      {/* Search Bar */}
      <TextField
        fullWidth
        placeholder="Search notes..."
        value={searchTerm}
        onChange={handleSearchChange}
        size="small"
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: 'background.paper',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.grey[400],
              },
            },
          },
        }}
       slotProps={{ input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: theme.palette.grey[400] }} />
            </InputAdornment>
          ),
          endAdornment: searchTerm ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleClearSearch}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        } }}/>

      {/* List Container */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Loading state */}
        {isLoading && notes.length === 0 ? (
          <Box>
            {Array.from({ length: 6 }).map((_, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  px: 3,
                  py: 2.5,
                  borderBottom: `1px solid ${theme.palette.grey[100]}`,
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Skeleton sx={{width: "70%", height: 22,  mb: 0.5 }} />
                  <Skeleton sx={{width: "40%", height: 18,  mb: 0.75 }} />
                  <Skeleton  sx={{ width: "90%", height: 16 }}/>
                  <Skeleton  sx={{ width: "60%", height: 16 }}/>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Skeleton  sx={{ width: 60, height: 18 }}/>
                </Box>
              </Box>
            ))}
          </Box>
        ) : isEmpty ? (
          <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
            <Typography variant="body1" color="text.secondary">
              {isSearchActive ? `No notes found for "${searchTerm}".` : emptyMessage}
            </Typography>
          </Box>
        ) : (
          <>
            {/* List */}
            <Box sx={{ position: 'relative' }}>
              {isLoading && notes.length > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    bgcolor: alpha(theme.palette.background.paper, 0.7),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1,
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}

              {notes.map(note => (
                <NoteListRow
                  key={note.note_id}
                  note={note}
                  onSelect={navigateToNote}
                  showCheckbox={false}
                  showIssuingUser={true}
                  hasNoteSigning={hasNoteSigning}
                  renderActions={cosignScope ? renderCosignActions : undefined}
                />
              ))}
            </Box>

            {/* Pagination */}
            {totalCount > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 3,
                  py: 1.5,
                  borderTop: `1px solid ${theme.palette.grey[100]}`,
                  bgcolor: alpha(theme.palette.grey[50], 0.5),
                }}
              >
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Page {page} of {totalPages}
                </Typography>

                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => setPage(page - 1)}
                    disabled={!hasPreviousPage || isLoading}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setPage(page + 1)}
                    disabled={!hasNextPage || isLoading}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
              </Box>
            )}
          </>
        )}
      </Paper>
    </>
  );
};
